import type { UserResolutionOptions } from "@repo/app-types/api/user";
import { CompanyInvitationErrorCode } from "@repo/app-types/entities/companyInvitation";
import { type FC, useEffect, useState } from "react";
import {
  useAcceptInvitationMutation,
  useJoinDomainMutation,
  useJoinViaSsoMutation,
} from "../../api/entities/user.js";
import { type OAuthProvider, startOAuthSignIn } from "../../api/oauthSignIn.js";
import { requiresPopupAuth } from "../../api/popupAuth.js";
import {
  isConflictQueryError,
  isUnauthorizedQueryError,
  readQueryErrorCode,
} from "../../api/queryErrors.js";
import { startSsoSignInWithProviderId } from "../../api/signInActions.js";
import { signoutUser } from "../../api/supabaseService.js";
import { useAuthPopupSession } from "../../hooks/useAuthPopupSession.js";
import { SALES_DEMO_LINK } from "../signin/signInPage.js";
import {
  AcceptInvitationScreen,
  AuthMethodRequiredScreen,
  JoinDomainScreen,
  NoOrganizationScreen,
  SsoInterstitialScreen,
} from "./accountResolutionScreens.js";

/**
 * Brief delay before auto-firing the SSO join, so a stuck/failed SSO redirect
 * is visible behind the spinner rather than flashing past.
 */
const AUTO_SSO_DELAY_MS = 1000;

/**
 * Shown for a 409 from any join flow: the email's teammate row is already
 * bound to a different company or auth account, which no retry can fix.
 */
const CONFLICT_ERROR_MESSAGE =
  "This email is already associated with a different organization on Alpharun. Sign out to use a different account, or contact support.";

/**
 * Shown for a 401 from a join flow: the target company doesn't accept the
 * session's sign-in method, so the user has to sign in differently first.
 */
const AUTH_METHOD_ERROR_MESSAGE =
  "This organization doesn't allow your current sign-in method. Sign out and sign in a different way.";

/** Maps a failed join mutation to user-facing copy. */
function computeJoinErrorMessage(
  error: unknown,
  fallbackMessage: string,
): string {
  if (isConflictQueryError(error)) {
    return CONFLICT_ERROR_MESSAGE;
  }
  if (isUnauthorizedQueryError(error)) {
    return AUTH_METHOD_ERROR_MESSAGE;
  }
  return fallbackMessage;
}

interface AccountResolutionProps {
  options: UserResolutionOptions;
  /** Invitation token from an `/accept-invitation?t=...` deep link, if any. */
  invitationToken: string | null;
  /** Refetches `GET /user` after a successful resolution. */
  refetchUser: () => void;
}

/**
 * Container for the account-resolution flow. Decides which presentational
 * screen to show for an unresolved session, runs the SSO auto-join, and wires
 * the resolution mutations + sign-out. The screens themselves live in
 * {@link ./accountResolutionScreens.js} and are storybook-renderable.
 */
export const AccountResolution: FC<AccountResolutionProps> = ({
  options,
  invitationToken,
  refetchUser,
}) => {
  const [joinViaSso] = useJoinViaSsoMutation();
  const [acceptInvitation, { isLoading: isAccepting }] =
    useAcceptInvitationMutation();
  const [joinDomain, { isLoading: isJoiningDomain }] = useJoinDomainMutation();
  const [error, setError] = useState<string | null>(null);
  // Detect iframe embedding once, out of the render phase.
  const [shouldUsePopupAuth] = useState(requiresPopupAuth);

  useAuthPopupSession({ isEnabled: shouldUsePopupAuth, onError: setError });

  const onSignOut = () => void signoutUser();
  const ssoAutoJoin = options.sso_auto_join;

  // Auto-fire the SSO join when the session is an SSO session that resolves to
  // a company. The interstitial (with spinner) shows while this runs.
  useEffect(() => {
    if (!ssoAutoJoin) {
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      void (async () => {
        try {
          await joinViaSso().unwrap();
          if (!cancelled) {
            refetchUser();
          }
        } catch (joinError) {
          if (!cancelled) {
            setError(
              isConflictQueryError(joinError)
                ? CONFLICT_ERROR_MESSAGE
                : isUnauthorizedQueryError(joinError)
                  ? "We couldn't synchronize your SSO account. Sign out and try once more; if the problem continues, contact your administrator or Alpharun support."
                  : "We couldn't finish signing you in via SSO.",
            );
          }
        }
      })();
    }, AUTO_SSO_DELAY_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [ssoAutoJoin, joinViaSso, refetchUser]);

  if (ssoAutoJoin) {
    return (
      <SsoInterstitialScreen
        companyName={ssoAutoJoin.company_name}
        errorMessage={error}
        onSignOut={onSignOut}
      />
    );
  }

  // Auth-method mismatch: the teammate belongs to a company that no longer
  // accepts this sign-in method. Direct them to an accepted method — social
  // OAuth when the company accepts it (no SSO configured), SSO otherwise.
  const mismatch = options.auth_method_mismatch;
  if (mismatch) {
    const ssoProviderId = mismatch.sso_provider_ids[0] ?? null;

    const onOAuthSignIn = (provider: OAuthProvider) => {
      setError(null);
      void (async () => {
        const { error: oauthError } = await startOAuthSignIn(provider, {
          usePopup: shouldUsePopupAuth,
        });
        if (oauthError) {
          setError("Couldn't start sign-in. Please try again.");
        }
      })();
    };

    const onSsoSignIn = () => {
      if (!ssoProviderId) {
        return;
      }
      setError(null);
      void (async () => {
        const { error: ssoError } = await startSsoSignInWithProviderId(
          ssoProviderId,
          { usePopup: shouldUsePopupAuth },
        );
        if (ssoError) {
          setError(ssoError);
        }
      })();
    };

    return (
      <AuthMethodRequiredScreen
        companyName={mismatch.company_name}
        acceptedMethods={mismatch.accepted_methods}
        ssoProviderIds={mismatch.sso_provider_ids}
        errorMessage={error}
        onGoogleSignIn={() => onOAuthSignIn("google")}
        onMicrosoftSignIn={() => onOAuthSignIn("azure")}
        onSsoSignIn={onSsoSignIn}
        onSignOut={onSignOut}
      />
    );
  }

  // Pending invitation (from the email match or an `/accept-invitation` link).
  const hasInvitation =
    Boolean(options.pending_invitation) || Boolean(invitationToken);
  if (hasInvitation) {
    const acceptToken =
      invitationToken ?? options.pending_invitation?.token ?? null;

    const onAccept = () => {
      setError(null);
      void (async () => {
        try {
          await acceptInvitation({ token: acceptToken }).unwrap();
          refetchUser();
        } catch (acceptError) {
          const errorCode = readQueryErrorCode(acceptError);
          if (errorCode === CompanyInvitationErrorCode.ALREADY_JOINED) {
            // Already a member of the invited company — refresh the
            // resolution instead of surfacing an error.
            refetchUser();
            return;
          }
          setError(
            errorCode === CompanyInvitationErrorCode.NO_EMAIL_MATCH
              ? "This invitation was sent to a different email address. Sign out and sign in with the invited email."
              : computeJoinErrorMessage(
                  acceptError,
                  "We couldn't accept this invitation. It may have expired — please ask your admin to invite you again.",
                ),
          );
        }
      })();
    };

    return (
      <AcceptInvitationScreen
        companyName={options.pending_invitation?.company_name}
        isJoining={isAccepting}
        errorMessage={error}
        onAccept={onAccept}
        onSignOut={onSignOut}
      />
    );
  }

  // Matching domain company: offer to join the company that owns the email's domain.
  const matchingDomainCompany = options.matching_domain_company;
  if (matchingDomainCompany) {
    const onJoin = () => {
      setError(null);
      void (async () => {
        try {
          await joinDomain().unwrap();
          refetchUser();
        } catch (domainJoinError) {
          setError(
            computeJoinErrorMessage(
              domainJoinError,
              "We couldn't add you to this organization. Please try again.",
            ),
          );
        }
      })();
    };

    return (
      <JoinDomainScreen
        companyName={matchingDomainCompany.company_name}
        isJoining={isJoiningDomain}
        errorMessage={error}
        onJoin={onJoin}
        onUseDifferentAccount={onSignOut}
      />
    );
  }

  // Last resort: no way to resolve — point the user at booking a demo.
  return (
    <NoOrganizationScreen
      onScheduleDemo={() => (window.location.href = SALES_DEMO_LINK)}
      onSignOut={onSignOut}
    />
  );
};
